import * as React from 'react';
import { MutationFunc } from 'react-apollo';
import { RouteComponentProps, Route, Redirect } from 'react-router-dom';
import { uniqBy } from 'lodash';
import { adopt } from 'react-adopt';
import Helmet from 'react-helmet';
import * as striptags from 'striptags';

import AuthService from '../../services/AuthService';
import FlashStore from '../../services/FlashStore';
import ErrorPage from '../ErrorPage';
import ProductDetail from '../../components/ProductDetail';
import { IVariantSizePair } from '../../components/ProductDetail/ProductDetail';
import ProductInfo from '../../components/ProductInfo';
import Favorites from '../../components/Query/Products/Favorites';
import FullVariantById from '../../components/Query/Products/FullVariantById';
import Categories from '../../components/Query/Categories/Categories';
import Attributes from '../../components/Query/Attributes/FilterableAttributes';
import Banners from '../../components/Query/Banners';
import { matchAttribute } from '../../helpers/attributes/matchAttribute';
import { Breadcrumbs, Message, Section } from '../../modules/modules';
import CountryService from '../../services/CountryService';
import LastVisited from '../../components/LastVisited';
import { getSize, canProductBuy } from '../../helpers/products';
import { getImagesFromProductByVariant } from '../../helpers/getImagesFromProductByVariant';
import Image from '../../components/Image';
import ReloadPage from '../../components/ReloadPage';
import GoogleTagManagerService from '../../services/GoogleTagManagerService';
import RecommendedProducts, { SepararelyLoadedRecommendedProducts } from '../../components/RecommendedProducts';
import { canUseDOM } from '../../utils';
import CategoryList from '../../components/Categories';
import './productDetail.scss';
import { ExternalBlog } from '../../components/ExternalBlog/ExternalBlog';
import { IBreadcrumbItem } from '../../interfaces/IBreadCrumbItem';
import {
  renderBreadCrumList as renderBreadCrumbListLDJSON,
  renderProduct as renderProductLDJSON,
} from '../../helpers/ld_json';
import ExternalBlogArticles from '../../components/Query/ExternalBlogArticles/ExternalBlogArticles';

const USE_CATEGORY_IN_PRODUCT_LINKS: boolean = false;
const SHOW_LAST_VISITED: boolean = false;
const SHOW_PRODUCT_INFO_IN_PRODUCT_DETAIL_SECTION: boolean = true;

// todo: přesunout do nějakého logického helperu
export const buildProductLink = (product: GQL.IProduct, category: GQL.ICategory | undefined, variant: GQL.IProductVariant, language: string) => {
  const { countryCode, currencyCodePageParam } = CountryService.GetInstance();
  const currencySuffix = currencyCodePageParam ? `?currency=${currencyCodePageParam}` : '';
  if (USE_CATEGORY_IN_PRODUCT_LINKS && (category !== undefined)) {
    if (category.slug === undefined) {
      return undefined;
    }

    // Country code urls are not used anymore
    // return `/${countryCode}/${category.slug[language]}/${product.slug[language]}/${variant.sku}`;
    return `/${category.slug[language]}/${product.slug[language]}/${variant.sku}${currencySuffix}`;
  }

  if (variant && !variant.id && product && product.variants && product.variants.length > 0) {
    // Country code urls are not used anymore
    // return `/${countryCode}/${product.slug[language]}/${product.variants[0].sku}`;
    return `/${product.slug[language]}/${product.variants[0].sku}${currencySuffix}`;
  }

  // Country code urls are not used anymore
  // return `/${countryCode}/${product.slug[language]}/${variant.sku}`;
  return `/${product.slug[language]}/${variant.sku}${currencySuffix}`;
};

const buildBreadcrumbs = (
  categoryId: string | undefined,
  product: GQL.IProduct,
  variant: GQL.IProductVariant,
  values: Array<IBreadcrumbItem> = [],
  language: string,
  targetCategory: GQL.ICategory,
  allCategories: Array<GQL.ICategory>
): Array<IBreadcrumbItem> => {
  const { countryCode, currencyCodePageParam } = CountryService.GetInstance();

  if (categoryId == undefined) {
    // Country code urls are not used anymore
    // values.unshift({ value: '', link: `/${countryCode}` });
    values.unshift({ value: '', link: '/' });
    values.push({
      value: `${product.name[language]}${product.subtitle && 'string' === typeof product.subtitle[language] ? ` ${product.subtitle[language]}` : ''}`,
      link: buildProductLink(product, targetCategory, variant, language)
    });

    return values;
  }

  const category = allCategories.find(c => c.id == categoryId);
  if (category == undefined) {
    return [];
    throw Error('I cant find category while building bredcrumbs');
  }

  values.unshift({
    value: category.name[language],
    // Country code urls are not used anymore
    // link: `/${countryCode}/products/${category != undefined && category.slug != undefined && category.slug[language]}`
    link: `/products/${category != undefined && category.slug != undefined && category.slug[language]}${currencyCodePageParam ? `?currency=${currencyCodePageParam}` : ''}`
  });

  const anotherCategoryId = category.parentCategoryId ? category.parentCategoryId : undefined;

  return buildBreadcrumbs(anotherCategoryId, product, variant, values, language, targetCategory, allCategories);
};

const ProductPrerequisitie = adopt({
  favourites: <Favorites />,
  categories: <Categories />,
  attributes: <Attributes />,
  banners: <Banners />
});

export interface IDetailProps extends RouteComponentProps<{ language: string; categorySlug: string; ean: string }> {
  toggleFavorite: MutationFunc;
  setCurrentProductAvailabityWatchdogForm?: (
    product?: GQL.IProduct,
    variant?: GQL.IProductVariant,
  ) => void;
}

class Detail extends React.Component<IDetailProps, any> {
  private getSSR(): JSX.Element | null {
    if (canUseDOM() && ('querySelector' in document)) {
      const ssrProductDetail = document.querySelector('.☒'); // has to correspond to type in <Section type="product-detail">
      const ssrProductInfo = document.querySelector('.☑');
      const ssrParts: JSX.Element[] = [];
      if (ssrProductDetail) {
        const ssrContent = ssrProductDetail.innerHTML;
        const ssrClasses = ssrProductDetail.className;
        ssrParts.push(<section className={ssrClasses} dangerouslySetInnerHTML={{ __html: ssrContent }} />);
      }
      if (ssrProductInfo) {
        const ssrContent = ssrProductInfo.innerHTML;
        const ssrClasses = ssrProductInfo.className;
        ssrParts.push(<div className={ssrClasses} dangerouslySetInnerHTML={{ __html: ssrContent }} />);
      }
      if (ssrParts.length > 0) {
        return (
          <React.Fragment>
            {ssrParts}
          </React.Fragment>
        );
      }
    }
    return null;
  }

  // Trying to put gray background to the right side of product detail
  /* public componentDidMount(): void {
    if ('addEventListener' in window) {
      window.addEventListener('resize', () => this.onResize());
    }
  }

  public componentWillUnmount(): void {
    window.removeEventListener('resize', () => this.onResize());
  }

  private onResize() {
    console.log('RESIZe');
    const el = document.querySelector('.⚓');
    if (el) {
      console.log(el);
      this.getElementDimensions(el);
    }
  }
*/  

  private getElementDimensions(el: Element | null) {
    if (!el) {
      return;
    }
    const currentHeight = this.state && this.state.elHeight;
    if (el && 'number' === typeof el.clientHeight) {
      if (el.clientHeight !== currentHeight) {
        const rect = el.getBoundingClientRect();
        this.setState({
          elHeight: el.clientHeight,
          elWidth: Math.max(0, window.innerWidth - rect.right - 15),
          elLeft: rect.right - (rect.x || 0)
        });
      }
    }
  }

  public render() {
    // Původní konstrukce pro načtení currencyId nedokázala ošetřit situaci, kdy currency ještě nebylo načtené
    // const { language, countryId, currency: { id: currencyId } } = CountryService.GetInstance();
    const { language, countryId, currency } = CountryService.GetInstance();

    if ((! currency)) {
      return this.getSSR();
    }

    const currencyId = currency.id;

    return (
      <main>
        <ProductPrerequisitie>
          {({ favourites, categories, attributes, banners }: any) => {
            const banner = banners.banners && banners.banners.banners && banners.banners.banners.length > 0
              ? banners.banners.banners.find((b: any) => b.type === 'PortfolioMessage')
              : null;

            return (
              <>
                {banner &&
                  <Message to={banner.link}>
                    {banner.title[language]}
                  </Message>
                }

                <FullVariantById
                  ean={this.props.match.params.ean}
                  currencyId={currencyId}
                  country={countryId}
                >
                  {({ fullVariant, createLastVisited }: any) => {
                    // in SSR, sometimes query ends with result { loading: true, networkStatus: 1, error: undefined } and no product detail is rendered
                    // if that happens, make next attempt to load the page
                    if ((global as any).SSR && fullVariant.loading) {
                      return <ReloadPage />;
                    }

                    if ((fullVariant.loading || fullVariant.variant === undefined)
                      || categories.categories.loading || attributes.attributes.loading) {
                      return this.getSSR();
                    }

                    const variant = fullVariant.variant as GQL.IProductVariant;

                    // varianta neexistuje
                    if (!variant || !variant.product) {
                      return (<ErrorPage title={`This product does not exists or is not in sell`} />);
                    }

                    let category: GQL.ICategory | undefined;
                    if (this.props.match.params.categorySlug) {
                      category = categories.categories.categories.find(
                        (c: GQL.ICategory) =>
                          c.slug !== undefined &&
                          c.slug[language] !== undefined &&
                          c.slug[language].toLowerCase() ==
                          this.props.match.params.categorySlug.toLowerCase()
                      );
                    }

                    const categoryForBreadcrumbs = category
                      ? category
                      : variant.product.categories && variant.product.categories.length > 0
                        ? variant.product.categories[0]
                        : undefined;

                    let ssrBredcrumbs: JSX.Element | null = null;
                    // The following would probably by rarely used
                    if (!categoryForBreadcrumbs && canUseDOM() && ('querySelector' in document)) {
                      const ssrProductBreadcrumbs = document.querySelector('div.breadcrumbs');
                      if (ssrProductBreadcrumbs) {
                        const ssrContent = ssrProductBreadcrumbs.innerHTML;
                        const ssrClasses = ssrProductBreadcrumbs.className;
                        ssrBredcrumbs = <div className={ssrClasses} dangerouslySetInnerHTML={{ __html: ssrContent }} />;
                      }
                    }

                    const browserURL = this.props.location;
                    const expectedURL = buildProductLink(variant.product, categoryForBreadcrumbs, variant, language);

                    // Redirect to non-category product URL, if necessary
                    if (expectedURL) {
                      const nonCategoryUrl = expectedURL.split('?')[0];
                      if (browserURL.pathname.includes(nonCategoryUrl)) {
                        if (nonCategoryUrl !== browserURL.pathname) {
                          return <Route
                            render={({staticContext}) => {
                              if (staticContext) {
                                staticContext.statusCode = 301;
                              }
                              return <Redirect to={`${nonCategoryUrl}${browserURL.search}`} />;
                            }}
                          />;
                        }
                      }

                      if (browserURL.pathname !== nonCategoryUrl) {
                        return <Route
                            render={({staticContext}) => {
                              if (staticContext) {
                                staticContext.statusCode = 308;
                              }
                              return <Redirect to={`${nonCategoryUrl}${browserURL.search}`} />;
                            }}
                          />;
                      }
                    }

                    const sizes = uniqBy(
                      this.getVariantSizePairs(
                        variant,
                        variant.product.variants,
                        attributes.attributes.attributes.filter((a: any) => a.tags && a.tags.includes('size')),
                        language
                      ) || [],
                      'size'
                    );

                    const { product } = variant;
                    const images = getImagesFromProductByVariant(product, variant);
                    const description = striptags(
                      ((product.description || {})[language] || '')
                        .replace(/\n/g, '')
                        .replace(/\u00a0/g, '')
                    );

                    // Zobrazení produktu do GTM
                    GoogleTagManagerService.virtualPageView('product');
                    // GTMItemListName nebrat z this.props.history.location.state, tam je state undefined
                    GoogleTagManagerService.viewProduct(variant.product, variant, this.props.location.state && this.props.location.state.GTMItemListName || undefined);

                    const breadCrumbsLinks: IBreadcrumbItem[] = categoryForBreadcrumbs && buildBreadcrumbs(
                      categoryForBreadcrumbs.id,
                      variant.product,
                      variant,
                      [],
                      language,
                      categoryForBreadcrumbs,
                      categories.categories.categories
                    ) || [];

                    return (
                      <Route
                        render={({staticContext}) => {
                          // Mark product page with special status to find them easily for caching
                          if (staticContext && 'string' === typeof process.env.REACT_APP_PRODUCT_PAGE_STATUS_CODE) {
                            // Here status code is used as a mark of product page, unexisting status code shoud be used
                            // it can be even a string, which makes cheking it in caching function easier
                            (staticContext.statusCode as any) = process.env.REACT_APP_PRODUCT_PAGE_STATUS_CODE;
                          }

                          if (this.props.match.params.categorySlug) {
                            const urlCategory = product.categories?.find((searchCategory) => {
                              if (!searchCategory.slug) {
                                return false;
                              }

                              for (const slugLang of Object.keys(searchCategory.slug)) {
                                if (searchCategory.slug[slugLang] === this.props.match.params.categorySlug) {
                                  return true;
                                }
                              }
                              return false;
                            });

                            if (!urlCategory && staticContext) {
                              // Just send not found status, but with regular product detail
                              staticContext.statusCode = 404;
                            }
                          }
                          return ( 
                      <>
                        <Helmet>
                          <title>{`${(product && product.metaTitle && product.metaTitle[language])
                            || (product && product.name && product.name[language])} for Sale | ${process.env.REACT_APP_PROJECT_NAME}`}</title>
                          {product && product.metaDescription && <meta name="description" content={`${product.metaDescription[language]}`} />}
                          <meta property="og:title" content={`${(product && product.metaTitle && product.metaTitle[language])
                            || (product && product.name && product.name[language])} for Sale | ${process.env.REACT_APP_PROJECT_NAME}`} />
                          {product && product.metaDescription && <meta property="og:description" content={`${(product && product.metaDescription[language])}`} />}
                          {images && images.length > 0 &&
                            <meta property="og:image" content={Image.createUrlImage({ image: images[0].file })} />}
                            {renderBreadCrumbListLDJSON(breadCrumbsLinks)}
                            {renderProductLDJSON(variant, `${this.props.location.pathname}${this.props.location.search}`)}
                        </Helmet>

                        <div className="☒">
                          {/* Upravená verze s menu zlobí pro malé šířky, pro ně ponechána původní varianta */}
                          <Section type="product-detail">
                            <div className="container">
                              <div className="row">
                                {categoryForBreadcrumbs &&
                                  <div className="col-12">
                                    <Breadcrumbs
                                      links={breadCrumbsLinks}
                                    />
                                  </div>
                                  || ssrBredcrumbs && <div className="col-12">
                                    {ssrBredcrumbs}
                                  </div>}

                                <div className="col-12">
                                  <ProductDetail
                                    isFavorite={this.isFavorite(variant.product.variants, favourites.favorites.favorites)}
                                    toggleFavorite={async (id: string) => {
                                      await this.toggleFavorite(id);
                                      favourites.favorites.refetch();
                                    }}
                                    availableSizeVariants={sizes}
                                    variant={variant}
                                    category={category}
                                    sell={variant.inStock > 0 || canProductBuy(variant.product) === true}
                                    createLastVisited={createLastVisited}
                                    setCurrentProductAvailabityWatchdogForm={this.props.setCurrentProductAvailabityWatchdogForm}
                                  />
                                </div>
                              </div>
                            </div>
                          </Section>

                          <ProductInfo
                            product={variant.product}
                            variant={variant}
                          />
                        </div>

                        <div className="☑">
                          <Section type="products">
                            <div className="container">
                              <div className="mainRow row ⛱">
                              <CategoryList />
                        <Section type="product-detail">
                          <div className="container">
                            <div className="row">
                              {categoryForBreadcrumbs &&
                                <div className="col-12">
                                  <Breadcrumbs
                                    links={buildBreadcrumbs(
                                      categoryForBreadcrumbs.id,
                                      variant.product,
                                      variant,
                                      [],
                                      language,
                                      categoryForBreadcrumbs,
                                      categories.categories.categories
                                    )}
                                  />
                                </div>
                                || ssrBredcrumbs && <div className="col-12">
                                  {ssrBredcrumbs}
                                </div>}

                              <div className="col-12 ⚓" /* ref={(el) => this.getElementDimensions(el)} */>
                                <ProductDetail
                                  isFavorite={this.isFavorite(variant.product.variants, favourites.favorites.favorites)}
                                  toggleFavorite={async (id: string) => {
                                    await this.toggleFavorite(id);
                                    favourites.favorites.refetch();
                                  }}
                                  availableSizeVariants={sizes}
                                  variant={variant}
                                  category={category}
                                  sell={variant.inStock > 0 || canProductBuy(variant.product) === true}
                                  createLastVisited={createLastVisited}
                                  setCurrentProductAvailabityWatchdogForm={this.props.setCurrentProductAvailabityWatchdogForm}
                                />
                              </div>
                              {SHOW_PRODUCT_INFO_IN_PRODUCT_DETAIL_SECTION && <div style={{backgroundColor: 'white', width: '100%'}} >
                                <ProductInfo
                                  product={variant.product}
                                  variant={variant}
                                />
                              </div> || ''}
                            </div>
                            {/* Try to put gray background to the right side of the product detail */ }
                            <div className="☔" style={{
                                height: this.state && `calc(${this.state.elHeight - 3}px + 5em)` || undefined,
                                width: this.state && this.state.elWidth || undefined,
                                left: this.state && this.state.elLeft || undefined
                                }} />
                          </div>
                        </Section>

                        {!SHOW_PRODUCT_INFO_IN_PRODUCT_DETAIL_SECTION && 
                        <ProductInfo
                          product={variant.product}
                          variant={variant}
                        /> || '' }

                              </div>
                            </div>
                          </Section>
                        </div>

                        {Array.isArray(product.recommendedProductVariants) && (product.recommendedProductVariants.length > 0) && (
                          <RecommendedProducts
                            variants={((recommended) => {
                              const variants: GQL.IProductVariant[] = [];
                              const s = recommended.sort((a, b) => (a.order || 0) - (b.order || 0)).map(rpv => rpv.productVariant);
                              // because TS does not understand that .filter(v => v) clears out possible null values
                              for (const v of s) {
                                if (v) {
                                  variants.push(v);
                                }
                              }
                              return variants;
                            })(product.recommendedProductVariants)}
                            GTMItemListName="Recommended products"
                          />
                        ) || product.id && (
                          <SepararelyLoadedRecommendedProducts productId={product.id} country={countryId || undefined} GTMItemListName="Recommended products"/>
                        )
                        || ''}

                        {Array.isArray(product.externalBlogTags) && (
                          <ExternalBlogArticles tags={product.externalBlogTags}>
                            {({ blogArticles }: any) => {
                              if (blogArticles.loading || blogArticles.error) {
                                return null;
                              }
                              return (
                                <ExternalBlog
                                  // blogTags={product.externalBlogTags} // Tohle povolí načítání blogů přímo na frontendu, čímž blogy nejsou v SSR
                                  articleCountShow={product.externalBlogArticleNumber}
                                  blogPreview={blogArticles.externalBlogArticles}
                                />
                              );
                            }}
                          </ExternalBlogArticles>
                        ) || ''}

                        {SHOW_LAST_VISITED && <LastVisited
                          without={variant.id}
                          GTMItemListName={'Last Visited'}
                        />
                        || ''}
                      </>
                          );
                        }}
                      />
                    );
                  }}
                </FullVariantById>
              </>
            );
          }}
        </ProductPrerequisitie>
      </main>
    );
  }

  private async toggleFavorite(variantId: string) {
    const { countryCode } = CountryService.GetInstance();

    if (this.props.toggleFavorite) {
      try {
        const result = await this.props.toggleFavorite({
          variables: {
            variantId
          }
        });
        if (result.data.toggleFavorite) {
          FlashStore.GetInstance().showMessage(
            'Úspěšně přidáno do oblíbených',
            'success',
            4000,
            `/${countryCode}/favorite`
          );
        } else {
          if (AuthService.GetInstance().loggedIn) {
            FlashStore.GetInstance().showMessage(
              'Odebráno z oblíbených',
              'success',
              4000,
              `/${countryCode}/favorite`
            );
          } else {
            FlashStore.GetInstance().showMessage('Nejdříve se přihlaste nebo zaregistrujte', 'failure');
          }
        }
      } catch (e) {
        FlashStore.GetInstance().showMessage('Nejdříve se přihlaste nebo zaregistrujte', 'failure');
      }
    }
  }

  private getVariantSizePairs(
    variant: GQL.IProductVariant,
    variants: Array<GQL.IProductVariant> | null,
    sizeAttributes: Array<GQL.IAttribute>,
    language: string
  ) {
    const sizes = new Array<IVariantSizePair>();
    let sizeAttribute: GQL.IAttribute | undefined;

    const siblingVariants = (variants ? [...variants] : [variant]);

    for (const v of siblingVariants) {
      sizeAttribute = matchAttribute(v, sizeAttributes);
      if (sizeAttribute) {
        break;
      }
    }
    if (sizeAttribute == undefined) {
      return undefined;
    }

    for (const v of siblingVariants) {
      const size = getSize(v, sizeAttributes, language);
      if (size) {
        sizes.push({
          variant: v,
          size
        });
      }
    }

    return sizes;
  }

  private getIdFromSlug(slug: string) {
    const splited = slug.split('-');
    const splitedUUID = splited.slice(splited.length - 5, splited.length);

    const uuid = splitedUUID.join('-');

    return uuid;
  }

  private isFavorite(variants: Array<GQL.IProductVariant> | null, favorites: Array<GQL.IProductVariant>) {
    for (const variant of variants || []) {
      if ((favorites || []).some(v => v.id == variant.id)) {
        return true;
      }
    }

    return false;
  }
}

export default Detail;



// WEBPACK FOOTER //
// ./src/pages/ProductDetail/ProductDetailPage.tsx